import {startsWith} from 'lodash';
import {Validator} from '../utils/Validator';
import {HttpClient} from '@angular/common/http';
import {Toaster, Type} from '../toaster/Toaster';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {Component, Inject, OnInit} from '@angular/core';
import {ApiServiceService} from '../api-service.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ContactUsComponent} from '../views/contact-us/contact-us.component';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Logger} from '../utils/Logger';
import { ConfigService } from '../service/config.service';

@Component({
    styleUrls: ['./campaign-pop-up.component.css'],
    templateUrl: './campaign-pop-up.component.html'
})
export class CampaignPopUpComponent extends ContactUsComponent implements OnInit {

    private static readonly MaxNameLength = 50;
    private static readonly CampaignQueryParamPrefix = 'utm_';

    public readonly contactUsFormGroup: FormGroup;
    public readonly queryParams?: Params;
    public message = 'I have a query. Please get in touch with me.';

    private readonly activatedRoute: ActivatedRoute;
    private readonly dialogRef: MatDialogRef<CampaignPopUpComponent>;

    constructor(httpClient: HttpClient, router: Router, apiService: ApiServiceService, validator: Validator,
                @Inject(MAT_DIALOG_DATA) queryParams: Params, dialogRef: MatDialogRef<CampaignPopUpComponent>,
                activatedRoute: ActivatedRoute, formBuilder: FormBuilder, configService: ConfigService) {
        super(httpClient, router, apiService, validator, configService);
        this.dialogRef = dialogRef;
        this.queryParams = queryParams;
        this.activatedRoute = activatedRoute;
        this.contactUsFormGroup = formBuilder.group({
            name: formBuilder.control('', [Validators.required, Validators.pattern(/[A-Za-z ]+/), Validators.maxLength(CampaignPopUpComponent.MaxNameLength)]),
            phoneNumber: formBuilder.control('', [Validators.required, CampaignPopUpComponent.validatePhoneNumber]),
            email: formBuilder.control('', [CampaignPopUpComponent.validateEmail]),
            message: formBuilder.control('I have a query. Please get in touch with me.', [Validators.required])
        });
    }

    private static validatePhoneNumber(control: AbstractControl): ValidationErrors | null {
        if (control.valid) {
            const phoneNumber = control.value;
            return new Validator().isMobilePhone(phoneNumber) ? null : {phone: 'invalid'};
        } else {
            return null;
        }
    }

    private static validateEmail(control: AbstractControl): ValidationErrors | null {
        const email = control.value;
        if (email) {
            return Validators.email(control);
        } else {
            return null;
        }
    }

    ngOnInit(): void {
        this.dialogRef.afterClosed().subscribe(() => this.removeQueryParamsForCampaign(this.queryParams));
    }

    public onCloseButtonClicked(): void {
        this.dialogRef.close();
    }

    public getErrorMessage(control: FormControl): string {
        const controlName = this.getControlName(control);
        const errors = {
            name: [{
                type: 'required',
                message: 'Please enter your name'
            }, {
                type: 'pattern',
                message: 'Please enter a valid name'
            }, {
                type: 'maxlength',
                message: `Please shorten your name to a maximum of ${CampaignPopUpComponent.MaxNameLength} characters`
            }],
            phoneNumber: [{
                type: 'required',
                message: 'Please enter your phone number'
            }, {
                type: 'phone',
                message: 'Please enter a valid phone number'
            }],
            email: [{
                type: 'email',
                message: 'Please enter a valid email address'
            }],
            message: [{
                type: 'required',
                message: 'Please enter a message'
            }]
        };

        let message = '';
        if (control.errors) {
            for (const occurredError of Object.keys(control.errors)) {
                const predefinedErrorsForControl = errors[controlName];
                const result = predefinedErrorsForControl.find(predefinedError => predefinedError.type === occurredError);
                if (result) {
                    message = result.message;
                    break;
                } else {
                    Logger.error(`Could not find error message for control [${controlName}] with error: ${occurredError}.`);
                }
            }
        }
        return message;
    }

    public async onContactFormSubmit(form: FormGroup, metadata?: Params): Promise<void> {
        FormGroupUtil.validateFormGroup(this.contactUsFormGroup);
        if (this.contactUsFormGroup.invalid) {
            return;
        }
        return this.submitContactForm(form, metadata);
    }

    protected validateFields(name: string, email: string, phoneNumber: string, message: string): string {
        if (!name || !(phoneNumber && this.validator.isMobilePhone(phoneNumber))) {
            return 'Please provide valid name and phone number';
        }

        if (email && !this.validator.isValidEmailFormat(email)) {
            return 'Please provide valid email address';
        }

        if (!message) {
            return 'Please provide a message';
        }
    }

    protected onSuccessfulSubmission(formGroup: FormGroup): void {
        super.onSuccessfulSubmission(formGroup);
        this.dialogRef.close();
        Toaster.toast('Thank you for contacting us. We will get in touch with you shortly.', Type.Success);
    }

    protected onErroneousSubmission(): void {
        Toaster.toast('Something went wrong while submitting your details. Please try again later.', Type.Error);
    }

    protected removeQueryParamsForCampaign(queryParams: Params): void {
        const filteredQueryParams = {};
        Object.keys(queryParams)
            .filter(key => !startsWith(key, CampaignPopUpComponent.CampaignQueryParamPrefix))
            .forEach(key => filteredQueryParams[key] = queryParams[key]);

        this.router.navigate([], {
            queryParams: filteredQueryParams
        });
    }

    private getControlName(c: AbstractControl): string | null {
        const formGroup = c.parent.controls;
        return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
    }

}
